import React, { useState, useEffect, useCallback } from 'react';
import { getCustomers, getCustomerDetail, getCustomerStatusLabel } from '../services/customerService';
import { formatDate } from '../utils/dateFormat';
import CustomerDetailPanel from '../components/common/CustomerDetailPanel';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import '../css/CustomerManagement.css';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');

  // Panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomers({
        page: pagination.page,
        size: pagination.size,
        status: filters.status,
        search: filters.search,
      });
      setCustomers(data.content || []);
      setPagination((prev) => ({
        ...prev,
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, filters]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleCustomerClick = async (customerId) => {
    setPanelOpen(true);
    setPanelLoading(true);
    try {
      const response = await getCustomerDetail(customerId);
      setSelectedCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer detail:', error);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
    setSelectedCustomer(null);
  };

  const handleStatusUpdate = () => {
    fetchCustomers();
    handleClosePanel();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-active';
      case 'INACTIVE':
        return 'status-inactive';
      case 'SUSPENDED':
        return 'status-suspended';
      case 'BANNED':
        return 'status-banned';
      default:
        return '';
    }
  };

  return (
    <div className="admin-layout">
      <Header />
      <PageHeader title="고객 관리" />
      <div className={`customer-management ${panelOpen ? 'panel-open' : ''}`}>
        <div className="main-content">
          <div className="filters-section">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                placeholder="고객 이름, 아이디, 전화번호로 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">검색</button>
            </form>

            <div className="total-count">
              총 {pagination.totalElements.toLocaleString()}개
            </div>
          </div>

          <div className="table-container">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>고객 이름</th>
                  <th>가입일</th>
                  <th>등록 아이디</th>
                  <th>계정상태</th>
                  <th>전화번호</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="loading-cell">로딩 중...</td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-cell">고객이 없습니다</td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer.customer_id}
                      onClick={() => handleCustomerClick(customer.customer_id)}
                      className="clickable-row"
                    >
                      <td>{customer.nickname}</td>
                      <td>{formatDate(customer.created_at)}</td>
                      <td>{customer.social_id}</td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(customer.status)}`}>
                          {getCustomerStatusLabel(customer.status)}
                        </span>
                      </td>
                      <td>{customer.phone || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(0)}
                disabled={pagination.page === 0}
              >
                {'<<'}
              </button>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
              >
                {'<'}
              </button>
              <span className="page-info">
                {pagination.page + 1} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
              >
                {'>'}
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages - 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
              >
                {'>>'}
              </button>
            </div>
          )}
        </div>

        {panelOpen && (
          <CustomerDetailPanel
            loading={panelLoading}
            customer={selectedCustomer}
            onClose={handleClosePanel}
            onStatusUpdate={handleStatusUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerManagement;
